import { InputHTMLAttributes, forwardRef, useId } from 'react'
import './Input.css'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Label text */
  label: string
  /** Error message to display */
  error?: string
  /** Help text displayed below input */
  helpText?: string
  /** Full width input */
  fullWidth?: boolean
}

/**
 * Input component with label and error handling
 * Derek-friendly design:
 * - Large touch targets (min 48px height)
 * - Clear labels
 * - High contrast error states
 * - Accessible
 */
const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helpText,
      fullWidth = false,
      className = '',
      id: providedId,
      ...props
    },
    ref
  ) => {
    const generatedId = useId()
    const id = providedId || generatedId
    const errorId = `${id}-error`
    const helpId = `${id}-help`

    const wrapperClasses = [
      'ps-input-wrapper',
      fullWidth && 'ps-input-wrapper--full-width',
      error && 'ps-input-wrapper--error',
      className,
    ]
      .filter(Boolean)
      .join(' ')

    return (
      <div className={wrapperClasses}>
        <label htmlFor={id} className="ps-input__label">
          {label}
          {props.required && <span className="ps-input__required">*</span>}
        </label>

        <input
          ref={ref}
          id={id}
          className="ps-input"
          aria-invalid={!!error}
          aria-describedby={error ? errorId : helpText ? helpId : undefined}
          {...props}
        />

        {error && (
          <p id={errorId} className="ps-input__error" role="alert">
            {error}
          </p>
        )}

        {helpText && !error && (
          <p id={helpId} className="ps-input__help">
            {helpText}
          </p>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'

export default Input
